import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Send, 
  Inbox, 
  Plus, 
  MoreHorizontal, 
  Eye, 
  Pencil, 
  Trash2, 
  Loader2,
  FileText,
  Building2,
  Calendar,
  Search,
  Filter,
  ArrowUpRight,
  ArrowDownLeft,
  X
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Transmittals = () => {
  const { token, hasPermission } = useAuth();
  const [transmittals, setTransmittals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDirection, setFilterDirection] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Related data for dropdowns
  const [contacts, setContacts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [documents, setDocuments] = useState([]);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTransmittal, setSelectedTransmittal] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    transmittal_number: '',
    description: '',
    direction: 'outgoing',
    contact_id: '',
    send_date: '',
    status_id: '',
    remark: '',
    document_ids: [],
    project_id: ''
  });

  const fetchTransmittals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/transmittals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransmittals(response.data.transmittals);
    } catch (error) {
      toast.error('Failed to load transmittals');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchRelatedData = useCallback(async () => {
    try {
      const [contactsRes, statusesRes, projectsRes, documentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/transmittal-statuses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/documents`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setContacts(contactsRes.data.contacts || []);
      setStatuses(statusesRes.data.transmittal_statuses || []);
      setProjects(projectsRes.data.projects || []);
      setDocuments(documentsRes.data.documents || []);
    } catch (error) {
      console.error('Failed to load related data');
    }
  }, [token]);

  useEffect(() => {
    fetchTransmittals();
    fetchRelatedData();
  }, [fetchTransmittals, fetchRelatedData]);

  const resetForm = () => {
    setFormData({
      transmittal_number: '',
      description: '',
      direction: 'outgoing',
      contact_id: '',
      send_date: format(new Date(), 'yyyy-MM-dd'),
      status_id: '',
      remark: '',
      document_ids: [],
      project_id: ''
    });
  };

  const handleCreate = async () => {
    if (!formData.direction) {
      toast.error('Please select a direction');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        transmittal_number: formData.transmittal_number || null
      };
      
      await axios.post(`${API_URL}/api/transmittals`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transmittal created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchTransmittals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transmittal');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedTransmittal) return;

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/transmittals/${selectedTransmittal.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transmittal updated successfully');
      setShowEditDialog(false);
      setSelectedTransmittal(null);
      fetchTransmittals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transmittal');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTransmittal) return;

    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/transmittals/${selectedTransmittal.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transmittal deleted successfully');
      setShowDeleteDialog(false);
      setSelectedTransmittal(null);
      fetchTransmittals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transmittal');
    } finally {
      setSaving(false);
    }
  };

  const openViewDialog = async (transmittal) => {
    try {
      const response = await axios.get(`${API_URL}/api/transmittals/${transmittal.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTransmittal(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load transmittal details');
    }
  };

  const openEditDialog = (transmittal) => {
    setSelectedTransmittal(transmittal);
    setFormData({
      transmittal_number: transmittal.transmittal_number || '',
      description: transmittal.description || '',
      direction: transmittal.direction || 'outgoing',
      contact_id: transmittal.contact_id || '',
      send_date: transmittal.send_date || '',
      status_id: transmittal.status_id || '',
      remark: transmittal.remark || '',
      document_ids: transmittal.document_ids || [],
      project_id: transmittal.project_id || ''
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (transmittal) => {
    setSelectedTransmittal(transmittal);
    setShowDeleteDialog(true);
  };

  const toggleDocument = (docId) => {
    setFormData(prev => ({
      ...prev,
      document_ids: prev.document_ids.includes(docId)
        ? prev.document_ids.filter(id => id !== docId)
        : [...prev.document_ids, docId]
    }));
  };

  // Filter transmittals
  const filteredTransmittals = transmittals.filter(t => {
    const matchesSearch = searchTerm === '' || 
      t.transmittal_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.contact?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDirection = filterDirection === 'all' || t.direction === filterDirection;
    const matchesStatus = filterStatus === 'all' || t.status_id === filterStatus;
    
    return matchesSearch && matchesDirection && matchesStatus;
  });

  const getDirectionBadge = (direction) => {
    if (direction === 'outgoing') {
      return (
        <Badge className="bg-blue-500/10 text-blue-600 gap-1">
          <ArrowUpRight className="w-3 h-3" />
          Outgoing
        </Badge>
      );
    }
    return (
      <Badge className="bg-green-500/10 text-green-600 gap-1">
        <ArrowDownLeft className="w-3 h-3" />
        Incoming
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    if (!status) return <Badge variant="outline">No Status</Badge>;
    return (
      <Badge style={{ backgroundColor: `${status.color}20`, color: status.color }}>
        {status.name}
      </Badge>
    );
  };

  return (
    <div className="animate-fade-in" data-testid="transmittals-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Transmittals</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage document transmittals to and from clients and subcontractors
          </p>
        </div>
        {hasPermission('admin') && (
          <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} data-testid="create-transmittal-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Transmittal
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search transmittals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-transmittals"
                />
              </div>
            </div>
            <Select value={filterDirection} onValueChange={setFilterDirection}>
              <SelectTrigger className="w-[150px]" data-testid="filter-direction">
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Directions</SelectItem>
                <SelectItem value="outgoing">Outgoing</SelectItem>
                <SelectItem value="incoming">Incoming</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transmittals Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredTransmittals.length === 0 ? (
            <div className="text-center py-12">
              <Send className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No transmittals found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || filterDirection !== 'all' || filterStatus !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first transmittal to get started'}
              </p>
              {hasPermission('admin') && (
                <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Transmittal
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Send Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead className="w-[70px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransmittals.map(transmittal => (
                  <TableRow key={transmittal.id}>
                    <TableCell className="font-mono font-medium">
                      <button
                        onClick={() => openViewDialog(transmittal)}
                        className="hover:text-primary hover:underline"
                      >
                        {transmittal.transmittal_number}
                      </button>
                    </TableCell>
                    <TableCell>{getDirectionBadge(transmittal.direction)}</TableCell>
                    <TableCell>
                      {transmittal.contact ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span>{transmittal.contact.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {transmittal.description || '-'}
                    </TableCell>
                    <TableCell>
                      {transmittal.send_date ? (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {format(new Date(transmittal.send_date), 'MMM d, yyyy')}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(transmittal.status)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="gap-1">
                        <FileText className="w-3 h-3" />
                        {transmittal.document_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`transmittal-actions-${transmittal.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openViewDialog(transmittal)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {hasPermission('admin') && (
                            <>
                              <DropdownMenuItem onClick={() => openEditDialog(transmittal)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openDeleteDialog(transmittal)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
          setSelectedTransmittal(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showEditDialog ? 'Edit Transmittal' : 'Create New Transmittal'}
            </DialogTitle>
            <DialogDescription>
              {showEditDialog 
                ? 'Update the transmittal details below'
                : 'Fill in the details to create a new transmittal'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Transmittal Number</Label>
                <Input
                  value={formData.transmittal_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, transmittal_number: e.target.value }))}
                  placeholder="Auto-generated if empty"
                  data-testid="input-transmittal-number"
                />
              </div>
              <div className="space-y-2">
                <Label>Direction *</Label>
                <Select 
                  value={formData.direction} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, direction: value }))}
                >
                  <SelectTrigger data-testid="select-direction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outgoing">
                      <div className="flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4" />
                        Outgoing (Send)
                      </div>
                    </SelectItem>
                    <SelectItem value="incoming">
                      <div className="flex items-center gap-2">
                        <ArrowDownLeft className="w-4 h-4" />
                        Incoming (Receive)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact (Client/Subcontractor)</Label>
                <Select 
                  value={formData.contact_id || 'none'} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, contact_id: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger data-testid="select-contact">
                    <SelectValue placeholder="Select contact" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {contacts.filter(c => c.is_active !== false).map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} ({c.contact_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Send Date</Label>
                <Input
                  type="date"
                  value={formData.send_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, send_date: e.target.value }))}
                  data-testid="input-send-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={formData.status_id || 'none'} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status_id: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger data-testid="select-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {statuses.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.code} - {s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Project</Label>
                <Select 
                  value={formData.project_id || 'none'} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, project_id: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger data-testid="select-project">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter transmittal description"
                rows={2}
                data-testid="input-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Remark</Label>
              <Textarea
                value={formData.remark}
                onChange={(e) => setFormData(prev => ({ ...prev, remark: e.target.value }))}
                placeholder="Additional remarks"
                rows={2}
                data-testid="input-remark"
              />
            </div>

            <div className="space-y-2">
              <Label>Linked Documents ({formData.document_ids.length} selected)</Label>
              <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                {documents.length === 0 ? (
                  <p className="p-4 text-center text-muted-foreground">No documents available</p>
                ) : (
                  <div className="divide-y">
                    {documents.map(doc => (
                      <label
                        key={doc.id}
                        className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer"
                      >
                        <Checkbox
                          checked={formData.document_ids.includes(doc.id)}
                          onCheckedChange={() => toggleDocument(doc.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{doc.name}</p>
                          <p className="text-xs text-muted-foreground">{doc.document_number}</p>
                        </div>
                        <Badge variant="outline" className="shrink-0">{doc.status}</Badge>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setShowEditDialog(false);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={showEditDialog ? handleUpdate : handleCreate} 
              disabled={saving}
              data-testid="save-transmittal-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {showEditDialog ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              {selectedTransmittal?.transmittal_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTransmittal && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {getDirectionBadge(selectedTransmittal.direction)}
                {getStatusBadge(selectedTransmittal.status)}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Contact</Label>
                  <p className="font-medium">
                    {selectedTransmittal.contact?.name || '-'}
                    {selectedTransmittal.contact?.contact_type && (
                      <span className="text-muted-foreground ml-2">
                        ({selectedTransmittal.contact.contact_type})
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Send Date</Label>
                  <p className="font-medium">
                    {selectedTransmittal.send_date 
                      ? format(new Date(selectedTransmittal.send_date), 'MMMM d, yyyy')
                      : '-'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Project</Label>
                  <p className="font-medium">{selectedTransmittal.project?.name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created By</Label>
                  <p className="font-medium">{selectedTransmittal.created_by_name || '-'}</p>
                </div>
              </div>

              {selectedTransmittal.description && (
                <div>
                  <Label className="text-muted-foreground">Description</Label>
                  <p>{selectedTransmittal.description}</p>
                </div>
              )}

              {selectedTransmittal.remark && (
                <div>
                  <Label className="text-muted-foreground">Remark</Label>
                  <p>{selectedTransmittal.remark}</p>
                </div>
              )}

              <div>
                <Label className="text-muted-foreground mb-2 block">
                  Linked Documents ({selectedTransmittal.documents?.length || 0})
                </Label>
                {selectedTransmittal.documents?.length > 0 ? (
                  <div className="border rounded-lg divide-y">
                    {selectedTransmittal.documents.map(doc => (
                      <div key={doc.id} className="flex items-center gap-3 p-3">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="font-medium">{doc.name}</p>
                          <p className="text-xs text-muted-foreground">{doc.document_number}</p>
                        </div>
                        <Badge variant="outline">{doc.status}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No documents linked</p>
                )}
              </div>

              <div className="text-xs text-muted-foreground pt-2 border-t">
                Created: {selectedTransmittal.created_at && format(new Date(selectedTransmittal.created_at), 'PPpp')}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Transmittal</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete transmittal &quot;{selectedTransmittal?.transmittal_number}&quot;? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transmittals;
